import { useMemo } from 'react';

import { setSessionStorageItem } from '@revenuemarket/browser';
import {
  AccountBalanceLine,
  BankFill,
  CardFill,
  CardLine,
  Colors,
  HomeFilled,
  HomeLine,
  MenuLine,
  SearchLine,
  typographyStyle,
  Flex,
  CDS_ZINDEX,
} from '@revenuemarket/cds';
import { Typography } from 'antd';
import { useRouter } from 'next/router';

import useGetIntegratedScrapeStatus from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import { SectionPressed } from '../../../styles/constant';

const BottomBarItem = ({
  icon,
  label,
  onClick,
  active,
  showNoti,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  showNoti?: boolean;
}) => {
  return (
    <Flex
      direction="column"
      style={{
        flex: 1,
      }}
      justify="center"
      align="center"
      onClick={onClick}
      className={css`
        padding: 4px 16px;
        height: 48px;
        &:active {
          background: ${Colors.grayOpacity04};
          backdrop-filter: blur(12px);
          border-radius: 16px;
        }
        ${SectionPressed}
      `}
    >
      <div
        style={{
          position: 'relative',
        }}
        className={css`
          & svg {
            fill: ${active ? Colors.gray900 : Colors.gray500};
          }
          & path {
            fill: ${active ? Colors.gray900 : Colors.gray500};
          }
        `}
      >
        {showNoti && (
          <div
            style={{
              position: 'absolute',
              top: 3,
              right: -1,
              width: 8,
              height: 8,
              backgroundColor: Colors.red500,
              borderRadius: '50%',
            }}
          />
        )}
        {icon}
      </div>
      <Typography.Text
        style={{
          ...typographyStyle['11_400'],
          color: active ? Colors.gray900 : Colors.gray500,
          width: 'max-content',
        }}
      >
        {label}
      </Typography.Text>
    </Flex>
  );
};

const MobileBottomBar = () => {
  const router = useRouter();
  const { companyId } = useCompanyId();
  const { data: scrapeStausData } = useGetIntegratedScrapeStatus(companyId as string);

  const scrapeErrorExist = useMemo(() => {
    const scrapeStatus = scrapeStausData?.Data?.data;
    if (!scrapeStatus) return false;

    const bankErrorNum =
      scrapeStatus.bankAccountScrapingStatus?.filter(
        (s) => s.status === 'ERROR' || s.status === 'FATAL',
      ).length ?? 0;

    const cardErrorNum =
      scrapeStatus.cardScrapingStatus?.filter((s) => s.status === 'ERROR' || s.status === 'FATAL')
        .length ?? 0;

    const taxInvoiceErrorExist = scrapeStatus.taxInvoiceScrapingStatus?.some(
      (s) => s.status === 'ERROR' || s.status === 'FATAL',
    );

    const cashReceiptErrorExist = scrapeStatus.cashReceiptScrapingStatus?.some(
      (s) => s.status === 'ERROR' || s.status === 'FATAL',
    );

    return bankErrorNum > 0 || cardErrorNum > 0 || taxInvoiceErrorExist || cashReceiptErrorExist;
  }, [scrapeStausData]);

  // 자주 사용되는 경로만 추출
  const currentPath = router.pathname;

  return (
    <Flex
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        padding: '8px 12px 8px 12px',
        zIndex: CDS_ZINDEX.MOBILE.HEADER,
        backgroundColor: Colors.white,
        borderTop: `1px solid ${Colors.grayOpacity04}`,
      }}
    >
      <BottomBarItem
        icon={currentPath === '/clobe/home' ? <HomeFilled /> : <HomeLine width={26} height={26} />}
        label="홈"
        onClick={() => {
          mixpanel.track(MIXPANEL_EVENT.MOBILE.CLICK_BOTTOM_NAV, {
            tab: 'home',
          });
          router.push('/clobe/home');
        }}
        active={currentPath === '/clobe/home'}
      />
      <BottomBarItem
        icon={
          <SearchLine
            width={26}
            height={26}
            fill={currentPath === '/clobe/search' ? Colors.gray900 : Colors.gray500}
          />
        }
        label="검색"
        onClick={() => {
          mixpanel.track(MIXPANEL_EVENT.MOBILE.CLICK_BOTTOM_NAV, {
            tab: 'search',
          });
          setSessionStorageItem('searchData', null);
          setSessionStorageItem('searchQuery', null);
          setSessionStorageItem('enteredSearchQuery', null);
          setSessionStorageItem('activeKey', 'all');
          if (currentPath !== '/clobe/search') {
            router.push('/clobe/search');
          } else {
            router.reload();
          }
        }}
        active={currentPath === '/clobe/search'}
      />
      <BottomBarItem
        icon={
          currentPath === '/clobe/transactions' ? (
            <BankFill />
          ) : (
            <AccountBalanceLine width={26} height={26} />
          )
        }
        label="은행 내역"
        onClick={() => {
          mixpanel.track(MIXPANEL_EVENT.MOBILE.CLICK_BOTTOM_NAV, {
            tab: 'transactions',
          });
          router.push('/clobe/transactions');
        }}
        active={currentPath === '/clobe/transactions'}
      />

      <BottomBarItem
        icon={
          currentPath === '/clobe/card-approval' ? (
            <CardFill />
          ) : (
            <CardLine width={26} height={26} />
          )
        }
        label="카드 내역"
        onClick={() => {
          mixpanel.track(MIXPANEL_EVENT.MOBILE.CLICK_BOTTOM_NAV, {
            tab: 'card-approval',
          });
          router.push('/clobe/card-approval');
        }}
        active={currentPath === '/clobe/card-approval'}
      />
      <BottomBarItem
        icon={<MenuLine />}
        label="메뉴"
        onClick={() => {
          mixpanel.track(MIXPANEL_EVENT.MOBILE.CLICK_BOTTOM_NAV, {
            tab: 'settings',
          });
          router.push('/clobe/data-manage');
        }}
        showNoti={scrapeErrorExist}
        active={currentPath === '/clobe/data-manage'}
      />
    </Flex>
  );
};

export default MobileBottomBar;
